import moment, { type Moment } from "moment";
import { type RouteComponentProps } from "react-router";

import { type IBaseEntity, type IIsSelectable } from "types/apiResponse/apiResponse";
import { type ILanguageConstants } from "types/constants";
import {
    type BTLoginTypes,
    type DirectoryType,
    type DocumentInstanceType,
    type FileLocation,
    type FileStatus,
    type ImageResizeMode,
    MediaType,
    type ObjectFit,
} from "types/enum";
import { BTFilePermissions, BTViewingPermissions } from "types/filePermissions";
import { rootLevelFolderId } from "types/MediaConstants";

import {
    EmptyStateMetadata,
    type GridSort,
    type IListRowData,
    ListEntity,
} from "utilities/list/list.types";

import { type BTServiceFileUpload } from "commonComponents/entity/media/AttachmentEntityPreview/AttachmentEntity.api.types";
import { type RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";

import { type EntityType, type FileRelations } from "handlers";

import { type BTFile as BTFileFromHandlers } from "handlers/models/bTFile";

import { getFileUrlFormatted } from "entity/media/common/media.utils";
import type { default as MediaListJson } from "entity/media/common/MediaList.api.json";

type MediaListEntityApiData = typeof MediaListJson;
type MediaListEntityInput = Omit<MediaListEntityApiData, "files"> & {
    files?: (FileApiData | BTFileFromHandlers)[] | null;
};
type BreadCrumbInformationApiData = MediaListEntityApiData["breadCrumbInformation"][number];
export type FileApiData = MediaListEntityApiData["files"][number];
type ExtraDataApiData = MediaListEntityApiData["extraData"];
type UserInfoFromExtraDataApiData = ExtraDataApiData["userInfo"];
type SignatureRequestsInfoApiData = FileApiData["signatureRequests"];
type AnnotationsInfoApiData = FileApiData["annotations"];
type RFIInfoApiData = NonNullable<FileApiData["rfis"]>[number];
type VideoPropertiesApiData = FileApiData["videoProperties"];
type PhotoPropertiesApiData = FileApiData["photoProperties"];
type DocumentPropertiesApiData = FileApiData["documentProperties"];
type AssociatedEntityApiData = FileApiData["associatedEntities"][number];
type FolderApiData = MediaListEntityApiData["folders"][number];
type FileInfoAggregateApiData = FolderApiData["fileInfo"];
type SpecialFolderExtraDataApiData = FolderApiData["specialFolderExtraData"];

// This is used as the id on our print pages that are used to generate a pdf using Gotenberg. We wait for the element with this id to be found
// before going ahead with the pdf generation. This ensures we have the content we need to display in the pdf.
export const pdfGenerationId = "printContent";

// Type for AnnotationInformation - this might come from a different API
interface IAnnotationInformationData {
    layerCount: number;
    signatureId?: number;
}

// Type for AnnotationLayer - this might come from a different API
interface IAnnotationLayerData {
    addedByDate: string | Date;
    annotationGroupId: number;
    annotationLayerId: number;
    annotationName: string;
    annotationType: AnnotationType;
}

// Type for AttachedMedia - this might come from a different API
interface IAttachedMediaData {
    associatedTypeName: string;
    docInstanceId: number;
    jobName: string;
    title: string;
}

// Type for BTServiceDocumentItem
interface IBTServiceDocumentItemData {
    id: number;
    tempId?: number;
    title?: string;
    fileName?: string;
    fileSize?: number;
    url: string;
    ext: string;
    mediaType: MediaType;
    brandedUrl?: string;
    showSubs: boolean;
    showOwner: boolean;
    canDelete: boolean;
    extraData?: unknown;
}

// Type for AnnotationLayerInfo
interface IAnnotationLayerInfoData {
    annotationId: number;
    title: string;
    docPath: string;
    downloadUrl: string;
}

// Type for DeletedFoldersResponse
interface IDeletedFoldersResponseData {
    totalFoldersCount: number;
    failedToDeleteCount: number;
    couldNotDeleteMessage: string;
}

export interface ISharedMediaProps {
    directoryType: DirectoryType;
    folderId: number;
}

export interface ISharedMediaRouteProps {
    directoryType: string;
    folderId: string;
}

export class AnnotationInformation {
    constructor(data: IAnnotationInformationData) {
        this.layerCount = data.layerCount;
        this.signatureId = data.signatureId;
    }

    layerCount: number;
    signatureId?: number;
}

export class AnnotationLayer {
    constructor(data: IAnnotationLayerData) {
        this.addedByDate = moment(data.addedByDate);
        this.annotationGroupId = data.annotationGroupId;
        this.annotationLayerId = data.annotationLayerId;
        this.annotationName = data.annotationName;
        this.annotationType = data.annotationType;
    }

    addedByDate: moment.Moment;
    annotationGroupId: number;
    annotationLayerId: number;
    annotationName: string;
    annotationType: AnnotationType;
}

export enum AnnotationType {
    PdfAnnotation = 1,
    PhotoDraw = 2,
    TempFile = 3,
    PdfSignature = 4,
}

export class AttachedMedia {
    constructor(data: IAttachedMediaData) {
        this.associatedTypeName = data.associatedTypeName;
        this.docInstanceId = data.docInstanceId;
        this.jobName = data.jobName;
        this.title = data.title;
    }

    associatedTypeName: string;
    docInstanceId: number;
    jobName: string;
    title: string;
}

export enum DocumentInstanceUpdateType {
    Overwrite = 0,
    CreateNewVersion = 1,
    NoDocumentChange = 2,
    Annotated = 3,
}

export enum GlobalMediaFolderIds {
    AttachedDocuments = -1,
    SubDocuments = -2,
    OwnerDocuments = -3,
    AttachedPhotos = -4,
    AttachedVideos = -5,
}

export class BTServiceDocumentItem<ExtraDataType = undefined> {
    constructor(data: IBTServiceDocumentItemData) {
        this.id = data.id;
        this.tempId = data.tempId;
        this.fileName = data.title || data.fileName || "";
        this.fileSize = data.fileSize || 0;
        this.url = data.url;
        this.ext = data.ext;
        this.mediaType = data.mediaType;
        this.brandedUrl = data.brandedUrl;
        this.showSubs = data.showSubs;
        this.showOwner = data.showOwner;
        this.canDelete = data.canDelete;
        if (data.extraData) {
            this.extraData = data.extraData as ExtraDataType;
        }
    }

    id: number;
    tempId?: number;
    fileName: string;
    fileSize: number;
    url: string;
    ext: string;
    mediaType: MediaType;
    brandedUrl?: string;
    showSubs: boolean;
    showOwner: boolean;
    canDelete: boolean;
    extraData?: ExtraDataType;
}

export class MediaListEntity extends ListEntity {
    constructor(data: MediaListEntityInput) {
        super(data);
        this.mediaEntry = new MediaEntry(data);
        this.listMetadata.emptyStatesMetadata = new EmptyStateMetadata({
            hasFilteredData: data.hasFilteredData ?? false,
            isNewToEntity: true,
            isReadOnly:
                this.mediaEntry.id === rootLevelFolderId
                    ? !this.mediaEntry.canAddFolders
                    : !this.mediaEntry.canAddFiles,
            helpLink: data.helpLink,
        });

        // Entity has records if any files or folders
        this.records =
            (this.mediaEntry.folders ? this.mediaEntry.folders.length : 0) +
            (this.mediaEntry.files ? this.mediaEntry.files.length : 0);
        this.listMetadata.hasData = this.records > 0;
    }

    /**
     * Creates a MediaListEntity from a files-only API response (unsigned documents).
     * Provides defaults for directory permissions and metadata that aren't part of the response.
     */
    static fromUnsignedDocuments(data: {
        builderId: number;
        jobId: number;
        currentFolderTitle: string;
        breadCrumbInformation: {
            folderId: string;
            title: string;
            directoryType: number;
            associatedType: number | null;
        }[];
        files: BTFileFromHandlers[];
    }): MediaListEntity {
        return new MediaListEntity({
            builderId: data.builderId,
            jobId: data.jobId,
            currentFolderId: 0,
            parentFolderId: 0,
            currentFolderTitle: data.currentFolderTitle,
            breadCrumbInformation: data.breadCrumbInformation,
            files: data.files,
            folders: [],
            canAddFiles: false,
            canAddFolders: false,
            canAdd: false,
            canUpdate: false,
            canDelete: false,
            canEmailFiles: false,
            canEmailFilesIn: false,
            canEditDailyLogs: false,
            canEditToDos: false,
            canEditBids: false,
            canEditScheduleItems: false,
            canEditChangeOrders: false,
            canEditSelections: false,
            canEditWarranties: false,
            hasOwnerUnreadDocs: false,
            jobHasOwners: false,
            jobHasSubs: false,
            isOwnerFeatureMediaSet: false,
            isSpecialFolder: false,
            allowSubOwnerUploads: false,
            allowedFileExtensions: [],
            helpLink: "",
            hasFilteredData: false,
            extraData: { userInfo: { firstName: "", lastName: "", builderName: "" } },
        });
    }

    mediaEntry: MediaEntry;
}

export class MediaEntry {
    constructor(data: MediaListEntityInput) {
        this.jobId = data.jobId!;
        this.builderId = data.builderId!;
        this.id = data.currentFolderId!;
        this.parentFolderId = data.parentFolderId!;
        this.canDelete = data.canDelete!;
        this.canAddFiles = data.canAddFiles!;
        this.canAddFolders = data.canAddFolders!;
        this.canEdit = data.canUpdate!;
        this.canEmailFiles = data.canEmailFiles!;
        this.canEmailFilesIn = data.canEmailFilesIn!;
        this.canEditDailyLogs = data.canEditDailyLogs!;
        this.canEditToDos = data.canEditToDos!;
        this.canEditScheduleItems = data.canEditScheduleItems!;
        this.canEditChangeOrders = data.canEditChangeOrders!;
        this.canEditBids = data.canEditBids!;
        this.canEditSelections = data.canEditSelections!;
        this.jobHasOwners = data.jobHasOwners!;
        this.jobHasSubs = data.jobHasSubs!;
        this.canEditWarranties = data.canEditWarranties!;
        this.allowSubOwnerUploads = data.allowSubOwnerUploads!;
        this.hasOwnerUnreadDocs = data.hasOwnerUnreadDocs!;
        this.isOwnerFeatureMediaSet = data.isOwnerFeatureMediaSet!;
        this.isSpecialFolder = data.isSpecialFolder!;
        this.allowedFileExtensions = data.allowedFileExtensions!;
        this.folders = data.folders
            ? data.folders.map((folder: FolderApiData) => new BTFolder(folder))
            : null;
        this.files = data.files ? data.files.map((file) => new BTFile(file)) : null;
        this.breadCrumbInformation = data.breadCrumbInformation
            ? data.breadCrumbInformation.map((breadCrumb) => new BreadCrumbInformation(breadCrumb))
            : null;
        this.title = data.currentFolderTitle!;
        this.extraData =
            data.extraData && data.extraData instanceof DirectoryExtraData
                ? new DirectoryExtraData(data.extraData)
                : undefined;
        this.lastRefreshed = moment();
        const fileCount = this.files ? this.files.length : 0;
        const folderCount = this.folders ? this.folders.length : 0;
        this.totalCount = fileCount + folderCount;
    }
    // IBaseEntity
    id: number;
    jobId: number;
    builderId: number;
    parentFolderId: number;

    breadCrumbInformation: BreadCrumbInformation[] | null;
    canAddFiles: boolean;
    canAddFolders: boolean;
    canDelete: boolean;
    canEdit: boolean;
    canEmailFiles: boolean;
    canEmailFilesIn: boolean;
    canEditDailyLogs: boolean;
    canEditToDos: boolean;
    canEditScheduleItems: boolean;
    canEditChangeOrders: boolean;
    canEditBids: boolean;
    canEditSelections: boolean;
    canEditWarranties: boolean;
    allowSubOwnerUploads: boolean;
    hasOwnerUnreadDocs: boolean;
    jobHasOwners: boolean;
    jobHasSubs: boolean;
    isOwnerFeatureMediaSet: boolean;
    isSpecialFolder: boolean;
    allowedFileExtensions: string[];
    folders: BTFolder[] | null;
    files: BTFile[] | null;
    totalCount: number;
    title: string;

    lastRefreshed: Moment;

    // Extra data
    extraData?: DirectoryExtraData;
}

export class BreadCrumbInformation {
    constructor(data: BreadCrumbInformationApiData) {
        this.folderId = data.folderId;
        this.title = data.title;
        this.directoryType = data.directoryType;
        this.associatedType = data.associatedType;
    }

    folderId: string;
    title: string;
    directoryType: DirectoryType;
    associatedType: DocumentInstanceType | null;
}

export interface IBaseFile {
    thumbnail?: string;
    title: string;
    extension: string;
    id: number;
    mediaType: MediaType;
}

export class BTFile implements IBaseFile, IBaseEntity, IListRowData, IIsSelectable {
    constructor(data: FileApiData | BTFileFromHandlers) {
        // Handle FileApiData (original implementation)
        const apiData = data as FileApiData;
        const docInstanceId = apiData.documentInstanceId;
        this.builderId = apiData.builderId;
        this.jobId = apiData.jobId;
        this.id = docInstanceId;
        this.documentId = apiData.documentId;
        this.title = apiData.title;
        this.friendlyFileName = apiData.friendlyFileName;
        // Note: The thumbnail may not have height and width set.
        // You will need to make to set a height and width if not use the Thumbnail component.
        this.thumbnail = apiData.thumbnail;
        this.extension = apiData.extension;
        this.docPath = getFileUrlFormatted(docInstanceId, apiData.extension, apiData.docPath);
        this.downloadUrl = getFileUrlFormatted(
            docInstanceId,
            apiData.extension,
            apiData.downloadUrl,
        );
        this.sizeInBytes = apiData.sizeInBytes;
        this.mediaType = apiData.mediaType;
        this.isTemplate = apiData.isTemplate;
        this.addedBy = apiData.addedBy;
        this.dateAttached = apiData.dateAttached ? moment(apiData.dateAttached) : null;
        this.dateTaken = apiData.dateTaken ? moment(apiData.dateTaken) : null;
        this.dateModified = apiData.dateModified ? moment(apiData.dateModified) : null;
        this.dateDeleted = apiData.dateDeleted ? moment(apiData.dateDeleted) : null;
        this.ownerViewedDate = apiData.ownerViewedDate ? moment(apiData.ownerViewedDate) : null;
        this.associatedEntities = apiData.associatedEntities
            ? (apiData.associatedEntities as AssociatedEntityApiData[]).map(
                  (e) => new AssociatedEntityInfo(e),
              )
            : [];
        this.viewPermissions = new BTViewingPermissions(apiData.viewPermissions);
        this.permissions = new BTFilePermissions(apiData.permissions);
        this.rfiCount = apiData.rfiCount;
        this.discussionCount = apiData.discussionCount;
        this.unreadDiscussionCount = apiData.unreadDiscussionCount;
        this.signatureRequests = apiData.signatureRequests
            ? new SignatureRequestsInfo(apiData.signatureRequests)
            : undefined;
        this.annotations = apiData.annotations
            ? new AnnotationsInfo(apiData.annotations)
            : undefined;
        this.rfis = apiData.rfis ? apiData.rfis.map((rfi) => new RFIInfo(rfi)) : undefined;
        this.videoProperties = apiData.videoProperties
            ? new VideoProperties(apiData.videoProperties)
            : undefined;
        this.photoProperties = apiData.photoProperties
            ? new PhotoProperties(apiData.photoProperties)
            : undefined;
        this.documentProperties = apiData.documentProperties
            ? new DocumentProperties(apiData.documentProperties)
            : undefined;
        this.docLocation = apiData.docLocation;
        this.fileRelations = apiData.fileRelations
            ? {
                  ...apiData.fileRelations,
                  userRelations: apiData.fileRelations.userRelations?.map((user) => ({
                      ...user,
                      loginType: user.loginType as BTLoginTypes | undefined,
                  })),
              }
            : undefined;

        this.isSelected = false;
    }

    // IListRowData
    id: number;
    documentId?: number;

    addedBy: string;
    annotations?: AnnotationsInfo;
    associatedEntities: AssociatedEntityInfo[];
    builderId: number;
    dateAttached: Moment | null;
    dateDeleted: Moment | null;
    dateTaken: Moment | null;
    dateModified: Moment | null;
    discussionCount: number;
    unreadDiscussionCount: number;
    readonly docPath: string;
    documentProperties?: DocumentProperties;
    readonly downloadUrl: string;
    extension: string;
    friendlyFileName: string;
    isTemplate: boolean;
    jobId: number;
    jobName: string;
    mediaType: MediaType;
    ownerViewedDate: Moment | null;
    permissions: BTFilePermissions;
    photoProperties?: PhotoProperties;
    rfiCount: number;
    rfis?: RFIInfo[];
    signatureRequests?: SignatureRequestsInfo;
    sizeInBytes: number;
    thumbnail: string;
    title: string;
    videoProperties?: VideoProperties;
    viewPermissions: BTViewingPermissions;
    fileRelations?: FileRelations;
    // IIsSelectable
    isSelected: boolean;
    docLocation: FileLocation;
}

export class BTFolder implements IBaseEntity, IListRowData, IIsSelectable {
    constructor(data: FolderApiData) {
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.folderId = data.folderId;
        this.id = data.folderId;
        this.parentFolderId = data.parentFolderId;

        this.title = data.title;
        this.dateAdded = data.dateAdded ? moment(data.dateAdded) : null;
        this.dateModified = data.dateModified ? moment(data.dateModified) : null;
        this.totalSize = data.totalSize;

        this.mediaType = data.mediaType;
        this.directoryType = data.directoryType;

        this.showSubs = data.showSubs;
        this.showOwner = data.showOwner;
        this.canUpload = data.canUpload;
        this.canUpdate = data.canUpdate;
        this.canDelete = data.canDelete;
        this.hasChildFolders = data.hasChildFolders;

        this.fileInfo = data.fileInfo ? new FileInfoAggregate(data.fileInfo) : null;
        this.specialFolderExtraData = new SpecialFolderExtraData(data.specialFolderExtraData);

        this.isSelected = false;
    }
    // IListRowData
    id: number;

    builderId: number;
    jobId: number;
    folderId: number;
    parentFolderId: number;

    title: string;
    dateAdded: Moment | null;
    dateModified: Moment | null;
    totalSize: number;

    mediaType: MediaType;
    directoryType: DirectoryType;

    showSubs: boolean;
    showOwner: boolean;
    canUpload: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    hasChildFolders: boolean;

    fileInfo: FileInfoAggregate | null;
    specialFolderExtraData: SpecialFolderExtraData;

    // IIsSelectable
    isSelected: boolean;
}

export interface IBTFileFolder {
    file: BTFile | null;
    folder: BTFolder | null;
    id: number;
    isSelected: boolean;
}

export class AssociatedEntityInfo {
    constructor(data: AssociatedEntityApiData) {
        this.associatedEntityId = data.associatedEntityId;
        this.associatedParentId = data.associatedParentId;
        this.associatedGrandParentId = data.associatedGrandParentId;
        this.associatedType = data.associatedType;
        this.associatedEntityTitle = data.associatedEntityTitle;
        this.externalUrl = data.externalUrl;
        this.relatedItemType = data.relatedItemType;
        this.externalId = data.externalId;
    }

    associatedEntityId: number;
    associatedParentId: number;
    associatedGrandParentId: number;
    associatedType: DocumentInstanceType;
    associatedEntityTitle: string;
    externalUrl: string | null;
    externalId: string | null;
    relatedItemType: RelatedItemType;
}

class FileInfoAggregate {
    constructor(data: FileInfoAggregateApiData) {
        this.annotationCount = data.annotationCount;
        this.discussionCount = data.discussionCount;
        this.rfiCount = data.rfiCount;
        this.signatureRequestTotal = data.signatureRequestTotal;
        this.signatureRequestsSigned = data.signatureRequestsSigned;
    }

    annotationCount: number;
    discussionCount: number;
    rfiCount: number;
    signatureRequestTotal: number;
    signatureRequestsSigned: number;
}

class SpecialFolderExtraData {
    constructor(data: SpecialFolderExtraDataApiData) {
        this.isSpecialFolder = data.isSpecialFolder;
        this.folderAssociatedType = data.folderAssociatedType;
    }
    isSpecialFolder: boolean;
    folderAssociatedType: DocumentInstanceType;
}

class SignatureRequestsInfo {
    constructor(data: SignatureRequestsInfoApiData) {
        if (data) {
            this.signatureRequestId = data.signatureRequestId;
            this.pending = data.pending;
            this.signed = data.signed;
            this.isAssignedToUser = data.isAssignedToUser;
            this.isAssignedToUserAndPending = data.isAssignedToUserAndPending;
            this.dueDate = data.dueDate ? moment(data.dueDate) : undefined;
        }
    }

    signatureRequestId: number;
    pending: number;
    signed: number;
    isAssignedToUser: boolean;
    isAssignedToUserAndPending: boolean;
    dueDate?: Moment;
}

class AnnotationsInfo {
    constructor(data: AnnotationsInfoApiData) {
        if (data) {
            this.annotationGroupId = data.annotationGroupId;
            this.layerCount = data.layerCount;
            this.layers = data.layers
                ? data.layers.map(
                      (layer: IAnnotationLayerInfoData) => new AnnotationLayerInfo(layer),
                  )
                : null;
            this.allLayerDocPath = data.allLayerDocPath;
        }
    }

    annotationGroupId?: number;
    layerCount: number;
    layers: AnnotationLayerInfo[] | null;
    allLayerDocPath: string;
}

export class AnnotationLayerInfo {
    constructor(data: IAnnotationLayerInfoData) {
        if (data) {
            this.annotationId = data.annotationId;
            this.title = data.title;
            this.docPath = data.docPath;
            this.downloadUrl = data.downloadUrl;
        }
    }

    annotationId: number;
    title: string;
    docPath: string;
    downloadUrl: string;
}

class RFIInfo {
    constructor(data: RFIInfoApiData) {
        this.rfiId = data.rfiId;
        this.titlePrefix = data.titlePrefix;
        this.title = data.title;
    }

    rfiId: number;
    titlePrefix: string;
    title: string;
}

class VideoProperties {
    constructor(data: VideoPropertiesApiData) {
        if (data) {
            this.videoId = data.videoId;
            this.videoStatus = data.videoStatus;
            if (data.length) {
                this.length = data.length;
            }
        }
    }

    videoId: number;
    length?: number;
    videoStatus: FileStatus;
}

class PhotoProperties {
    constructor(data: PhotoPropertiesApiData) {
        if (data) {
            this.isOriginalResolution = data.isOriginalResolution;
            this.is360Media = data.is360Media;
            this.viewingOrder = data.viewingOrder ? data.viewingOrder : null;
            this.drawingUrl = data.drawingUrl;
        }
    }

    isOriginalResolution: boolean;
    is360Media: boolean;
    viewingOrder: number | null;
    drawingUrl?: string;
}

export class DocumentProperties {
    constructor(data: DocumentPropertiesApiData) {
        if (data) {
            this.isMacroEnabledExcelFile = data.isMacroEnabledExcelFile;
        }
    }

    isMacroEnabledExcelFile: boolean;
}

export class DirectoryExtraData {
    constructor(data: ExtraDataApiData) {
        if (data) {
            this.userInfo = new UserInformation(data.userInfo);
        }
    }
    userInfo: UserInformation;
}

export class UserInformation {
    constructor(data: UserInfoFromExtraDataApiData) {
        if (data) {
            this.firstName = data.firstName;
            this.lastName = data.lastName;
            this.builderName = data.builderName;
        }
    }
    firstName: string;
    lastName: string;
    builderName: string;
}

export interface IFileFolderListRequest {
    folderId?: number;
    mediaType?: MediaType;
    associatedTypeId?: DocumentInstanceType;
    directoryType?: DirectoryType;
    loginType?: BTLoginTypes;
    jobId: number;
}

export interface IMediaDeleteRequest {
    folderIds: number[];
    fileIds: number[];
    jobId: number;
    mediaType: MediaType;
}

export class DeletedFoldersResponse {
    constructor(data: IDeletedFoldersResponseData) {
        this.totalFoldersCount = data.totalFoldersCount;
        this.failedToDeleteCount = data.failedToDeleteCount;
        this.couldNotDeleteMessage = data.couldNotDeleteMessage;
    }
    totalFoldersCount: number;
    failedToDeleteCount: number;
    couldNotDeleteMessage: string;
}

export interface IDownloadZipOfFolderRequest {
    jobId: number;
    mediaType: MediaType;
    parentFolderId: number;
    folderIds?: number[];
    documentIds?: number[];
    titleOverride?: string;
    isDeleted?: boolean;
}

export interface IDownloadZipOfAttachedFilesRequest {
    mediaType: MediaType;
    documentInstanceTypes: DocumentInstanceType[];
    jobId: number;
    documentIds?: number[];
    titleOverride?: string;
}

export interface IDownloadZipOfAttachedFilesForEntityRequest {
    documentInstanceType: DocumentInstanceType;
    entityId: number;
    jobId?: number;
    titleOverride?: string;
}

export interface IFolderPermissionExtraData {
    canShowSubs: boolean;
    canShowOwner: boolean;
}

export type SelectedMediaListViews = "Table" | "Tile";

export type MediaListTabs = "folders" | "files";

export interface IFocusState {
    id?: number;
    type: "folder" | "file";
    shouldReveal?: boolean;
}

export const OSFileDragType = "Files";
export const BTFileDragType = "btfile";
export const BTFolderDragType = "btfolder";

export interface IBTFolderDragPayload {
    folderId: number;
}

export interface IBTFileDragPayload {
    fileId: number;
}

export interface IMediaListViewProps extends RouteComponentProps {
    allowBTMediaDrop: boolean;
    areAllVisibleFilesSelected: boolean;
    areAllVisibleFoldersSelected: boolean;
    areAllVisibleFilesFoldersSelected?: boolean;
    areCheckedActionsForFolders: boolean;
    areFilesExpanded: boolean;
    areFoldersExpanded: boolean;
    entity: MediaEntry;
    focusState: IFocusState;
    isInEditMode?: boolean;
    mediaType: MediaType;
    currentSort?: IMediaListSort;
    filesLoaded?: number;
    directoryName: string;
    resizeMode?: ImageResizeMode;
    fit?: ObjectFit;
    showFolderDetails?: boolean;
    isTrash: boolean;
    isTemplateMode: boolean;
    onFileSortChanged: (sort: GridSort) => void;
    onFolderSortChanged: (sort: GridSort) => void;
    onFileFolderSortChanged?: (sort: GridSort) => void;
    onChangeSortOrder?: (sortedPhotos: BTFile[]) => void;
    onChangeFileFolderSortOrder?: (sortedPhotos: IBTFileFolder[]) => void;
    onDeleteFileClick: (file: BTFile) => Promise<void>;
    onDeleteFolderClick: (folderId: number, jobId: number) => Promise<void>;
    onDownloadFileClick?: (file: BTFile) => Promise<void>;
    onDownloadFolderClick?: (
        folderId: number,
        directoryType: DirectoryType,
        mediaType: MediaType,
        jobId: number,
        parentFolderId: number,
        folderAssociatedType?: DocumentInstanceType,
    ) => Promise<void>;
    onDragDataReceived: (targetFolderId: number, dataTransfer: DataTransfer) => void;
    onEditOnline?: (file: BTFile) => Promise<void>;
    onCreateSignatureRequest?: (file: BTFile) => Promise<void>;
    onRenameFile: (documentInstanceId: number, title: string) => Promise<void>;
    onRenameFolder: (folderId: number, title: string) => Promise<void>;
    onClose?: () => void;
    onExpandChange: (tab: MediaListTabs, isExpanded: boolean) => void;
    onMediaModalOpen: (startingId: number) => void;
    onSelectedChanged: (
        mediaEntry: MediaEntry,
        areAllVisibleFoldersSelected?: boolean,
        areAllVisibleFilesSelected?: boolean,
        areAllVisibleFilesFoldersSelected?: boolean,
    ) => Promise<void>;
    setBTMediaDropAbility: (allowBTMediaDrop: boolean) => void;
    setFocus: (focusState: IFocusState) => void;
    onGenerateFileQRCode: (file: BTFile) => void;
    onGenerateFolderQRCode: (folder: BTFolder) => void;
    onAttachToEntity: (entityType: EntityType, file: BTServiceFileUpload) => void;
    onShareClick: (docInstanceId: number) => void;
    hideHeaderRow?: boolean;
    scrollableTargetId?: string;
}

export interface IMediaListUrlProps {
    jobId: string;
    directoryType: string;
    folderId: string;
}

export const MediaTypeNames = new Map<MediaType, ILanguageConstants>([
    [
        MediaType.Document,
        {
            singular: "Document",
            plural: "Documents",
        },
    ],
    [
        MediaType.Photo,
        {
            singular: "Photo",
            plural: "Photos",
        },
    ],
    [
        MediaType.Video,
        {
            singular: "Video",
            plural: "Videos",
        },
    ],
]);

export const defaultMediaTypeNames: ILanguageConstants = {
    singular: "File",
    plural: "Files",
};

export const getMediaTypeNames = (mediaType: MediaType) => {
    return mediaType === MediaType.Document
        ? defaultMediaTypeNames
        : (MediaTypeNames.get(mediaType) ?? defaultMediaTypeNames);
};

export interface IZohoSessionInfo {
    sessionDeleteUrl?: string;
    saveUrl?: string;
    sessionId?: string;
    documentDeleteUrl?: string;
    documentId?: string;
    documentUrl?: string;
}

export interface IFolderMenuItemsVisibility {
    showFolderDetails: boolean;
    showEditFolderProperties: boolean;
    showDownload: boolean;
    showCopy: boolean;
    showMove: boolean;
    showDelete: boolean;
    areSomeMenuItemsShown: boolean;
    showGenerateQRCode: boolean;
    showFolderSharing: boolean;
}

export interface IMediaListSort {
    fileSort?: GridSort;
    folderSort?: GridSort;
}

export interface IAttachToEntityDetails {
    entityType: EntityType;
    files: BTServiceFileUpload[];
}

export interface ICheckedActionData {
    selectedFolders: number[];
    selectedFiles: number[];
    selectedVideoIds: number[];
    jobId: number;
    documentInstanceTypes?: DocumentInstanceType[];
}
